 
import axios from "axios";
import React, { useEffect, useState, useContext, useCallback, useMemo, useRef } from "react";
import { FaHeart, FaRegHeart, FaShoppingCart, FaTags, FaUpload, FaTrash, FaBoxes, FaCheckCircle } from "react-icons/fa";
import { useParams } from "react-router-dom";
import "swiper/css";
import ProductComparison from "../Components/ProductComparison";
import AboutCompany from "../Components/productListcomponents/companyContent";
import { showErrorToast, showSuccessToast } from "../utils/toaster";
import { baseurl } from "../config/url";
import { DataProvider } from "../Components/ContextProvider";

const ACCEPTED_MIME = [
  "image/jpeg", "image/png", "image/webp",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];
const MAX_FILES = 10;
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_TOTAL_SIZE = 50 * 1024 * 1024;

const humanSize = (bytes = 0) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const ProductDetails = () => {
  const { productId } = useParams();

  const [product, setProduct] = useState(null);
  const [similarProducts, setSimilarProducts] = useState([]);
  const [error, setError] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [isInWishlist, setIsInWishlist] = useState(false);
  const [popupMessage, setPopupMessage] = useState(null);
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [showSellerDetails, setShowSellerDetails] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [zoomStyle, setZoomStyle] = useState({});
  const [zoomVisible, setZoomVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ---- Auth UI helpers ----
  const { issetLoginDialogOpen, setisSignup, setshowotpcomponent } = useContext(DataProvider);
  const promptLoginDialog = () => {
    setisSignup(false);
    setshowotpcomponent(false);
    issetLoginDialogOpen(true);
  };

  // ---- Prefill user details from localStorage ----
  useEffect(() => {
    const credentials = localStorage.getItem("usercredentials");
    if (credentials) {
      try {
        const parsed = JSON.parse(credentials);
        setUserDetails({
          name: parsed.name || "",
          email: parsed.email || "",
          phone: parsed.mobile || "",
          location: "",
        });
      } catch (err) {
        console.error("Failed to parse usercredentials from localStorage:", err);
      }
    }
  }, []);

  // ---- Location helpers ----
  const getLocationFromIP = useCallback(async () => {
    try {
      const res = await fetch("https://ipapi.co/json/");
      const data = await res.json();
      return `${data.city}, ${data.region}, ${data.country_name}`;
    } catch (err) {
      console.error("IP-based location fetch failed:", err);
      return "Unknown";
    }
  }, []);

  const getUserLocation = useCallback(async () => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        return resolve(getLocationFromIP());
      }
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          try {
            const res = await fetch(
              `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
            );
            const data = await res.json();
            const location = `${data.city || data.locality}, ${data.principalSubdivision}, ${data.countryName}`;
            resolve(location);
          } catch (err) {
            console.error("Reverse geocoding failed:", err);
            resolve(getLocationFromIP());
          }
        },
        async (error) => {
          console.warn("Geolocation permission denied or unavailable:", error.message);
          resolve(await getLocationFromIP());
        }
      );
    });
  }, [getLocationFromIP]);

  // ---- API helpers ----
  const recordUserInterest = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token || !productId) return;
    try {
      const location = await getUserLocation();
      await axios.post(
        `${baseurl}/api/user/interested`,
        { productId, location },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error("Failed to record interest:", error?.response?.data || error.message);
    }
  }, [productId, getUserLocation]);

  const getWishlist = useCallback(async () => {
    const userId = localStorage.getItem("userId");
    if (!userId) return;
    try {
      const response = await axios.get(`${baseurl}/api/wishlist/get-wishlist/${userId}`);
      const data = response?.data?.items || [];
      const isProductExist = data.some((p) => p?.productId?._id === productId);
      setIsInWishlist(isProductExist);
    } catch (error) {
      console.error("Error fetching wishlist:", error);
    }
  }, [productId]);

  useEffect(() => {
    const fetchProductDetails = async () => {
      try {
        const response = await axios.get(`${baseurl}/api/seller/details/${productId}`);
        setProduct(response.data);
        setQuantity(response.data.minquantity || 1);
        setSelectedImage(response.data.image);
        fetchSimilarProducts(response.data.name);
      } catch (err) {
        console.error("Error fetching product details:", err);
        setError("Error fetching product details.");
      }
    };

    if (productId) fetchProductDetails();
    recordUserInterest();
  }, [productId, recordUserInterest]);

  useEffect(() => {
    getWishlist();
  }, [getWishlist]);

  const fetchSimilarProducts = async (productName) => {
    try {
      const response = await axios.get(
        `${baseurl}/api/seller/get-similar-products?name=${encodeURIComponent(productName)}`
      );
      const res = response.data;
      const formattedData = Array.isArray(res) ? res : Array.isArray(res.products) ? res.products : [];
      setSimilarProducts({ products: formattedData });
    } catch (err) {
      console.error("Error fetching similar products:", err);
    }
  };

  // ---- UI Handlers ----
  const handleAddToWishlist = async () => {
    const userId = localStorage.getItem("userId");
    try {
      if (isInWishlist) {
        await axios.post(`${baseurl}/api/wishlist/remove`, { userId, productId });
        setIsInWishlist(false);
        showSuccessToast("Removed from wishlist");
      } else {
        await axios.post(`${baseurl}/api/wishlist/add`, { userId, productId });
        setIsInWishlist(true);
        showSuccessToast("Added to wishlist");
      }
    } catch (err) {
      showErrorToast("Error managing wishlist");
    }
  };

  const handleAddToCart = async () => {
    const userId = localStorage.getItem("userId");
    if (!userId) {
      showErrorToast("Please login to proceed!");
      return;
    }
    try {
      const location = await getUserLocation();
      const response = await axios.post(`${baseurl}/api/cart/add-to-cart`, {
        userId,
        location,
        items: [{ productId, quantity }],
      });

      if (response.data.message && response.data.message.includes("available for sale directly by the seller")) {
        setPopupMessage(response.data.message);
        setSelectedSeller(response.data.sellerDetails);
      } else {
        showSuccessToast(`${product.name} added to cart!`);
      }
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message || "An error occurred while adding to the cart. Please try again.";
      showErrorToast(errorMessage);
    }
  };

  const closePopup = () => {
    setPopupMessage(null);
    setSelectedSeller(null);
  };

  const handleShowSellerDetails = () => {
    setShowSellerDetails(true);
    setPopupMessage(null);
  };

  const increaseQuantity = () => {
    if (!product) return;
    if (quantity < product.maxquantity && quantity < product.stock) {
      setQuantity(quantity + 1);
    } else if (quantity >= product.stock) {
      showSuccessToast(`Only ${product.stock} items are available in stock. Please adjust your order.`);
    } else {
      showSuccessToast(`You can only order up to ${product.maxquantity} items. Thank you for understanding!`);
    }
  };

  const decreaseQuantity = () => {
    if (product && quantity > product.minquantity) {
      setQuantity(quantity - 1);
    } else {
      showSuccessToast(`The minimum quantity you can order is ${product.minquantity}. Adjust accordingly!`);
    }
  };

  const allImages = [
    ...(product?.image ? [product.image] : []),
    ...(product?.images || []),
  ];

  const handleMouseMove = (e) => {
    const bounds = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - bounds.left) / bounds.width) * 100;
    const y = ((e.clientY - bounds.top) / bounds.height) * 100;
    setZoomStyle({
      backgroundImage: `url(${selectedImage})`,
      backgroundPosition: `${x}% ${y}%`,
      backgroundSize: "300%",
    });
  };

  // ======== Bulk Drawer (Best Price / Bulk) ========    
  const [showBulk, setShowBulk] = useState(false);
  const [bulkPurpose, setBulkPurpose] = useState("best_price"); // "best_price" | "bulk"
  const openBulk = (purpose) => {
    const userId = localStorage.getItem("userId");
    if (!userId) return promptLoginDialog();
    setBulkPurpose(purpose);
    setShowBulk(true);
  };
  const closeBulk = () => setShowBulk(false);

  // Bulk form state (based on your BulkRequest component)
  const INITIAL_FORM = {
    fullName: "",
    email: "",
    phone: "",
    company: "",
    city: "",
    pincode: "",
    quantity: "",
    preferredBrands: "",
    requirement: "",
    agree: false,
  };
  const [form, setForm] = useState(INITIAL_FORM);
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [bulkErrors, setBulkErrors] = useState({});
  const [bulkStatus, setBulkStatus] = useState({ type: "", msg: "" });
  const fileInputRef = useRef(null);

  // Prefill name/email/phone when drawer opens
  useEffect(() => {
    if (showBulk) {
      setForm((prev) => ({
        ...prev,
        fullName: userDetails?.name || prev.fullName,
        email: userDetails?.email || prev.email,
        phone: userDetails?.phone || prev.phone,
        requirement:
          prev.requirement ||
          (product ? `Product: ${product.name} (${product.category?.name || ""})` : ""),
      }));
      setBulkStatus({ type: "", msg: "" });
      setBulkErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showBulk]);

  // Clean up blob URLs
  useEffect(() => {
    return () => {
      previews.forEach((p) => p?.url?.startsWith("blob:") && URL.revokeObjectURL(p.url));
    };
  }, [previews]);

  const totalSize = useMemo(() => files.reduce((s, f) => s + (f?.size || 0), 0), [files]);

  const onBulkChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
    setBulkErrors((er) => ({ ...er, [name]: "" }));
  };

  const validateBulk = () => {
    const er = {};
    if (!form.fullName.trim()) er.fullName = "Name is required";
    if (!/^\+?[0-9\s-]{7,15}$/.test(form.phone.trim())) er.phone = "Valid phone is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) er.email = "Valid email is required";
    if (form.pincode && !/^\d{6}$/.test(form.pincode)) er.pincode = "Pincode must be 6 digits";
    if (!form.requirement.trim()) er.requirement = "Please describe your requirement";
    if (!form.agree) er.agree = "Please accept terms to proceed";
    if (files.length > MAX_FILES) er.files = `Max ${MAX_FILES} files allowed`;
    if (totalSize > MAX_TOTAL_SIZE) er.files = `Total size must be under ${humanSize(MAX_TOTAL_SIZE)}`;
    setBulkErrors(er);
    return Object.keys(er).length === 0;
  };

  const pickBulkFiles = (picked) => {
    const incoming = Array.from(picked || []);
    if (!incoming.length) return;

    const valid = [];
    for (const f of incoming) {
      const okType = ACCEPTED_MIME.includes(f.type);
      const okSize = f.size <= MAX_FILE_SIZE;
      if (!okType || !okSize) continue;
      valid.push(f);
    }

    const combined = [...files, ...valid].slice(0, MAX_FILES);
    setFiles(combined);

    const makePreview = (f) => ({
      url: f.type.startsWith("image/") ? URL.createObjectURL(f) : "",
      name: f.name,
      size: f.size,
      type: f.type,
      isImage: f.type.startsWith("image/"),
    });

    const newPreviews = [...previews, ...valid.map(makePreview)].slice(0, MAX_FILES);
    setPreviews(newPreviews);
    setBulkStatus({ type: "", msg: "" });
    setBulkErrors((er) => ({ ...er, files: "" }));
  };

  const onBulkFileInputChange = (e) => pickBulkFiles(e.target.files);
  const onBulkDrop = (e) => {
    e.preventDefault();
    pickBulkFiles(e.dataTransfer.files);
  };
  const onBulkRemoveFile = (idx) => {
    const copyFiles = [...files];
    const copyPrev = [...previews];
    const removed = copyPrev[idx];
    copyFiles.splice(idx, 1);
    copyPrev.splice(idx, 1);
    if (removed?.url && removed.url.startsWith("blob:")) URL.revokeObjectURL(removed.url);
    setFiles(copyFiles);
    setPreviews(copyPrev);
  };
  const resetBulkForm = () => {
    previews.forEach((p) => p?.url?.startsWith("blob:") && URL.revokeObjectURL(p.url));
    setFiles([]);
    setPreviews([]);
    setForm({
      fullName: "",
      email: "",
      phone: "",
      company: "",
      city: "",
      pincode: "",
      quantity: "",
      preferredBrands: "",
      requirement: "",
      agree: false,
    });
    setBulkErrors({});
    if (fileInputRef.current) fileInputRef.current.value = null;
  };

  const submitBulk = async () => {
    if (!validateBulk()) {
      setBulkStatus({ type: "error", msg: "Please fix the highlighted fields." });
      return;
    }
    try {
      setIsSubmitting(true);

      const fd = new FormData();
      // purpose
      fd.append("purpose", bulkPurpose === "bulk" ? "bulk" : "best_price");
      // user info
      fd.append("fullName", form.fullName);
      fd.append("email", form.email);
      fd.append("phone", form.phone);
      fd.append("company", form.company);
      fd.append("city", form.city);
      fd.append("pincode", form.pincode);
      fd.append("quantity", form.quantity);
      fd.append("preferredBrands", form.preferredBrands);
      fd.append("requirement", form.requirement);
      // product context
      fd.append("productId", productId);
      fd.append("saleType", product?.saleType || "");
      const sellerId = product?.seller?._id || product?.sellerId || "";
      if (sellerId) fd.append("sellerId", sellerId);
      // location
      const safeLocation =
        (userDetails?.location && userDetails.location.trim()) || (await getUserLocation());
      fd.append("location", safeLocation || "Unknown");
      // files
      files.forEach((f) => fd.append("files", f));

      // Auth header if present (optional)
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

      const res = await axios.post(`${baseurl}/api/bulk-request`, fd, {
        headers,
      });

      if (res?.data) {
        setBulkStatus({
          type: "success",
          msg: res?.data?.message || "Your request has been submitted successfully!",
        });
        showSuccessToast("Request submitted. Our team will contact you shortly.");
        // clear after short delay
        setTimeout(() => {
          closeBulk();
          resetBulkForm();
          setBulkStatus({ type: "", msg: "" });
        }, 800);
      }
    } catch (err) {
      console.error("Bulk submit error:", err?.response?.data || err);
      setBulkStatus({
        type: "error",
        msg: err?.response?.data?.message || "Failed to submit. Please try again.",
      });
      showErrorToast(err?.response?.data?.message || "Submission failed.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- File handlers (small quick picker under details for bestprice SaleType) ---
  const [formData, setFormData] = useState({ files: [] });
  const handleFileChange = (e) => {
    const picked = Array.from(e.target.files || []);
    const allowedMimes = new Set([
      "image/png", "image/jpeg", "image/jpg", "image/webp", "image/gif",
      "application/pdf", "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ]);
    const maxSize = 5 * 1024 * 1024;
    for (const f of picked) {
      if (!allowedMimes.has(f.type)) {
        showErrorToast(`"${f.name}" is not allowed. Upload JPG/PNG/WebP/GIF, PDF, DOC or DOCX.`);
        return;
      }
      if (f.size > maxSize) {
        showErrorToast(`"${f.name}" exceeds 5MB. Please upload a smaller file.`);
        return;
      }
    }
    setFormData({ files: picked });
  };

  const numericPrice = Number(product?.price);
  const hasPrice = Number.isFinite(numericPrice) && numericPrice > 0;

  return (
    <div className="container mx-auto px-4 py-8 mt-24 capitalize">
      {error && <p className="text-red-500 text-center">{error}</p>}

      {product ? (
        <>
          <div className="grid md:grid-cols-2 gap-8">
            {/* Left: Images */}
            <div className="flex space-x-4">
              <div className="flex-shrink-0">
                <div className="space-y-4">
                  {allImages.map((image, index) => (
                    <img
                      key={index}
                      src={image}
                      alt={`Thumbnail ${index + 1}`}
                      onClick={() => setSelectedImage(image)}
                      className={`rounded-lg shadow-md max-w-24 max-h-24 object-cover cursor-pointer border-2 ${
                        selectedImage === image ? "border-blue-500" : "border-transparent"
                      }`}
                    />
                  ))}
                </div>
              </div>

              <div className="flex-grow">
                {selectedImage && (
                  <div
                    className="relative w-full"
                    onMouseMove={handleMouseMove}
                    onMouseEnter={() => setZoomVisible(true)}
                    onMouseLeave={() => setZoomVisible(false)}
                  >
                    <img
                      src={selectedImage}
                      alt={product.name}
                      className="rounded-lg shadow-md w-full object-cover"
                      loading="lazy"
                      decoding="async"
                      width="800"
                      height="600"
                      sizes="(max-width:640px) 100vw, (max-width:1024px) 80vw, 800px"
                      fetchpriority="auto"
                    />
                    {zoomVisible && (
                      <div
                        className="absolute top-0 left-full ml-4 w-[500px] h-[500px] border border-gray-300 rounded shadow-lg bg-no-repeat bg-white z-50"
                        style={zoomStyle}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Right: Details */}
            <div>
              {/* Title + Wishlist */}
              <div className="flex justify-between items-start gap-4">
                <h3 className="text-3xl font-bold leading-tight">{product.name}</h3>
                {isInWishlist ? (
                  <button
                    aria-label="Remove from wishlist"
                    onClick={handleAddToWishlist}
                    className="p-2 rounded-full bg-rose-50 text-rose-600 hover:bg-rose-100"
                    title="Remove from wishlist"
                  >
                    <FaHeart size={20} />
                  </button>
                ) : (
                  <button
                    aria-label="Add to wishlist"
                    onClick={handleAddToWishlist}
                    className="p-2 rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200"
                    title="Add to wishlist"
                  >
                    <FaRegHeart size={20} />
                  </button>
                )}
              </div>

              {/* Price row with inline 'Ask for Best Price' trigger */}
              <div className="mt-3 flex items-center flex-wrap gap-3">
                {hasPrice ? (
                  <p className="text-2xl font-semibold text-gray-900">
                    Price: ₹{numericPrice.toLocaleString("en-IN")}
                    {product?.priceUnit && (
                      <span className="ml-2 text-sm font-medium text-gray-600">
                        / {product.priceUnit.replace(/^Per/, "")}
                      </span>
                    )}
                  </p>
                ) : (
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-sm font-medium">
                    <FaTags />
                    Best price available on request
                  </div>
                )}

                {/* Inline Best Price trigger always available */}
                <button
                  onClick={() => openBulk("best_price")}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 text-sm font-medium hover:bg-emerald-100"
                >
                  <FaTags />
                  Ask for Best Price
                </button>
              </div>

              {/* Stock & Meta */}
              <p className="text-gray-600 mt-3 uppercase">{product.stock} in stock</p>

              <div className="mt-4 space-y-2 text-gray-700">
                <p>
                  <span className="font-semibold">Description: </span>
                  {product.description || "No description available."}
                </p>
                <p>
                  <span className="font-semibold">Category: </span>
                  {product.category?.name}
                </p>
                <p>
                  <span className="font-semibold">Subcategory: </span>
                  {product.subcategory?.name}
                </p>
                <p>
                  <span className="font-semibold">Min Qty: </span>
                  {product.minquantity}
                </p>
                <p>
                  <span className="font-semibold">Max Qty: </span>
                  {product.maxquantity}
                </p>

                {product.specifications && product.specifications.length > 0 && (
                  <div className="pt-2">
                    <h4 className="font-medium text-lg text-gray-800">Specifications</h4>
                    <ul className="list-disc ml-6 text-gray-700">
                      {product.specifications.map((spec, index) => (
                        <li key={index}>
                          <strong>{spec.key}:</strong> {spec.value}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Sale Type & Hire Charges */}
                <p className="text-lg font-semibold text-gray-800 mt-2">Sale Type: {product.saleType}</p>

                {product.saleType && product.saleType.trim().toLowerCase() === "sale by seller" ? (
                  <>
                    <p className="text-gray-700">
                      <strong>Sold By:</strong> {product.seller?.companyName}
                    </p>

                    {product.saleProduct === "Hire" && (
                      <div className="text-gray-700 mt-2">
                        <p className="font-medium">
                          This product is available for <strong>Hire</strong>.
                        </p>
                        <p className="text-sm mt-1">
                          Click <strong>Add to Cart</strong> to proceed with hiring.
                        </p>
                        <ul className="mt-2 space-y-1">
                          {product.hireCharges?.perDay > 0 && (
                            <li>
                              <strong>Per Day:</strong> ₹{product.hireCharges.perDay}
                            </li>
                          )}
                          {product.hireCharges?.perWeek > 0 && (
                            <li>
                              <strong>Per Week:</strong> ₹{product.hireCharges.perWeek}
                            </li>
                          )}
                          {product.hireCharges?.perMonth > 0 && (
                            <li>
                              <strong>Per Month:</strong> ₹{product.hireCharges.perMonth}
                            </li>
                          )}
                        </ul>
                      </div>
                    )}
                  </>
                ) : null}
              </div>

              

              {/* Quantity */}
              <div className="mt-6">
                <div className="text-sm text-gray-600 mb-1">Quantity</div>
                <div className="inline-flex items-center">
                  <button
                    className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-l hover:bg-gray-200"
                    onClick={decreaseQuantity}
                    aria-label="Decrease quantity"
                  >
                    –
                  </button>
                  <input
                    type="text"
                    value={quantity}
                    readOnly
                    className="w-14 text-center border-y border-gray-300 py-2"
                    aria-live="polite"
                  />
                  <button
                    className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-r hover:bg-gray-200"
                    onClick={increaseQuantity}
                    aria-label="Increase quantity"
                  >
                    +
                  </button>
                </div>
              </div>

              {/* ACTIONS */}
              <div className="mt-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {/* Primary action: switches based on price */}
                  {hasPrice ? (
                    <button
                      onClick={handleAddToCart}
                      className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-blue-600 text-white font-medium shadow hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-600"
                    >
                      <FaShoppingCart />
                      Add to Cart
                    </button>
                  ) : (
                    <button
                      onClick={() => openBulk("best_price")}
                      className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-emerald-600 text-white font-medium shadow hover:bg-emerald-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-emerald-600"
                    >
                      <FaTags />
                      Ask for Best Price
                    </button>
                  )}

                  {/* Secondary action: Ask for Bulk */}
                  <button
                    onClick={() => openBulk("bulk")}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700 font-medium shadow hover:bg-emerald-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-emerald-600"
                  >
                    <FaTags />
                    Ask for Bulk
                  </button>
                </div>

                <p className="mt-2 text-xs text-gray-500">Quick response • Verified sellers</p>
              </div>
            </div>
          </div>

          {/* Seller message Popup */}
          {popupMessage && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-white p-8 rounded-lg shadow-lg w-96">
                <p className="text-gray-700">{popupMessage}</p>
                <div className="flex justify-end mt-6 gap-4">
                  <button
                    className="px-6 py-2 bg-blue-600 text-white rounded-md shadow hover:bg-blue-700 transition"
                    onClick={handleShowSellerDetails}
                  >
                    OK
                  </button>
                  <button
                    className="px-6 py-2 bg-red-600 text-white rounded-md shadow hover:bg-red-700 transition"
                    onClick={closePopup}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Seller Details Popup */}
          {showSellerDetails && selectedSeller && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-lg w-[90%] max-w-md p-6 relative">
                <button
                  className="absolute top-4 right-4 text-gray-500 hover:text-gray-800"
                  onClick={() => setShowSellerDetails(false)}
                >
                  ✖
                </button>

                <div className="text-center">
                  <h3 className="text-xl font-semibold text-gray-800">Seller Information</h3>
                  <p className="text-gray-600 mt-2">Know more about the seller of this product.</p>
                </div>

                <div className="mt-6 space-y-4">
                  <div className="flex items-center">
                    <span className="font-semibold text-gray-700 w-1/3">Name:</span>
                    <span className="text-gray-600">{selectedSeller.name}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="font-semibold text-gray-700 w-1/3">Email:</span>
                    <span className="text-gray-600">{selectedSeller.email}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="font-semibold text-gray-700 w-1/3">Mobile:</span>
                    <span className="text-gray-600">{selectedSeller.mobile}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="font-semibold text-gray-700 w-1/3">Company:</span>
                    <span className="text-gray-600">{selectedSeller.companyName}</span>
                  </div>
                </div>

                <div className="flex justify-end mt-6 space-x-4">
                  <button
                    className="px-6 py-2 bg-red-600 text-white rounded-md shadow hover:bg-red-700 transition"
                    onClick={() => setShowSellerDetails(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Similar products / comparison */}
          <div className="mt-20">
            <ProductComparison
              data={
                similarProducts.products && similarProducts.products.length > 0
                  ? similarProducts
                  : { products: [product] }
              }
            />
          </div>
        </>
      ) : (
        !error && <p className="text-center text-xl">Loading product details...</p>
      )}

      <div className="mt-20">
        <AboutCompany />
      </div>

      {/* Global Submit Loader */}
      {isSubmitting && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow text-center">
            <p className="text-lg font-medium text-gray-700">Submitting your request...</p>
            <div className="mt-4 loader ease-linear rounded-full border-8 border-t-8 border-gray-200 h-12 w-12 animate-spin mx-auto"></div>
          </div>
        </div>
      )}

      {/* ======= Bulk Drawer (slides up modal) ======= */}
      {showBulk && (
        <div className="fixed inset-0 z-50">
          {/* backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={closeBulk}
            aria-label="Close bulk panel"
            role="button"
          />
          {/* panel */}
          <div className="absolute inset-x-0 bottom-0 max-h-[92vh] overflow-auto rounded-t-2xl bg-white shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-white">
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-slate-800">
                  {bulkPurpose === "bulk" ? "Ask for Bulk" : "Ask for Best Price"}
                </h3>
                <p className="text-[11px] sm:text-xs text-slate-600">
                  Share your details and (optionally) attach drawings, BOQs, or references.
                </p>
              </div>
              <button
                onClick={closeBulk}
                className="rounded-md px-3 py-1.5 text-sm border border-slate-300 hover:bg-slate-50"
              >
                Close
              </button>
            </div>

            <div className="px-5 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Full Name */}
                <div>
                  <label className="block text-[11px] sm:text-xs font-medium text-slate-700 mb-1">Full Name *</label>
                  <input
                    type="text"
                    name="fullName"
                    value={form.fullName}
                    onChange={onBulkChange}
                    className={`w-full rounded-lg border px-3 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 ${
                      bulkErrors.fullName ? "border-red-500 focus:ring-red-500" : "border-slate-300 focus:ring-blue-500"
                    }`}
                    placeholder="Enter your name"
                  />
                  {bulkErrors.fullName && <p className="text-red-600 text-[11px] sm:text-xs mt-1">{bulkErrors.fullName}</p>}
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-[11px] sm:text-xs font-medium text-slate-700 mb-1">Mobile Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={onBulkChange}
                    className={`w-full rounded-lg border px-3 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 ${
                      bulkErrors.phone ? "border-red-500 focus:ring-red-500" : "border-slate-300 focus:ring-blue-500"
                    }`}
                    placeholder="+91 9XXXXXXXXX"
                  />
                  {bulkErrors.phone && <p className="text-red-600 text-[11px] sm:text-xs mt-1">{bulkErrors.phone}</p>}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-[11px] sm:text-xs font-medium text-slate-700 mb-1">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={onBulkChange}
                    className={`w-full rounded-lg border px-3 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 ${
                      bulkErrors.email ? "border-red-500 focus:ring-red-500" : "border-slate-300 focus:ring-blue-500"
                    }`}
                    placeholder="name@example.com"
                  />
                  {bulkErrors.email && <p className="text-red-600 text-[11px] sm:text-xs mt-1">{bulkErrors.email}</p>}
                </div>

                {/* Company */}
                <div>
                  <label className="block text-[11px] sm:text-xs font-medium text-slate-700 mb-1">Company / Organization</label>
                  <input
                    type="text"
                    name="company"
                    value={form.company}
                    onChange={onBulkChange}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Company name (optional)"
                  />
                </div>

                {/* City */}
                <div>
                  <label className="block text-[11px] sm:text-xs font-medium text-slate-700 mb-1">City</label>
                  <input
                    type="text"
                    name="city"
                    value={form.city}
                    onChange={onBulkChange}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Delivery city"
                  />
                </div>

                {/* Pincode */}
                <div>
                  <label className="block text-[11px] sm:text-xs font-medium text-slate-700 mb-1">Pincode</label>
                  <input
                    type="text"
                    name="pincode"
                    value={form.pincode}
                    onChange={onBulkChange}
                    maxLength={6}
                    className={`w-full rounded-lg border px-3 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 ${
                      bulkErrors.pincode ? "border-red-500 focus:ring-red-500" : "border-slate-300 focus:ring-blue-500"
                    }`}
                    placeholder="6-digit pincode"
                  />
                  {bulkErrors.pincode && <p className="text-red-600 text-[11px] sm:text-xs mt-1">{bulkErrors.pincode}</p>}
                </div>

                {/* Quantity */}
                <div>
                  <label className="block text-[11px] sm:text-xs font-medium text-slate-700 mb-1">Quantity / Volume</label>
                  <input
                    type="text"
                    name="quantity"
                    value={form.quantity}
                    onChange={onBulkChange}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., 500 bags cement, 10 MT TMT, etc."
                  />
                </div>

                {/* Preferred Brands */}
                <div>
                  <label className="block text-[11px] sm:text-xs font-medium text-slate-700 mb-1">Preferred Brands (optional)</label>
                  <input
                    type="text"
                    name="preferredBrands"
                    value={form.preferredBrands}
                    onChange={onBulkChange}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., UltraTech, JSW, TATA, ACC, Dr. Fixit..."
                  />
                </div>

                {/* Requirement */}
                <div className="md:col-span-2">
                  <label className="block text-[11px] sm:text-xs font-medium text-slate-700 mb-1">Requirement Details *</label>
                  <textarea
                    name="requirement"
                    value={form.requirement}
                    onChange={onBulkChange}
                    rows={4}
                    className={`w-full rounded-lg border px-3 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 ${
                      bulkErrors.requirement ? "border-red-500 focus:ring-red-500" : "border-slate-300 focus:ring-blue-500"
                    }`}
                    placeholder="Describe your requirement, site details, delivery timeline, etc."
                  />
                  {bulkErrors.requirement && <p className="text-red-600 text-[11px] sm:text-xs mt-1">{bulkErrors.requirement}</p>}
                </div>

                {/* Files */}
                <div className="md:col-span-2">
                  <label className="block text-[11px] sm:text-xs font-medium text-slate-700 mb-1">
                    Attach Files (Images / PDF / DOC / XLS)
                  </label>
                  <div
                    onDrop={onBulkDrop}
                    onDragOver={(e) => e.preventDefault()}
                    className={`flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed p-6 text-center ${
                      bulkErrors.files ? "border-red-400 bg-red-50" : "border-slate-300 bg-slate-50"
                    }`}
                  >
                    <FaUpload className="text-2xl sm:text-3xl text-slate-500" />
                    <div className="text-[11px] sm:text-xs text-slate-600">
                      Drag &amp; drop files here, or{" "}
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="ml-1 text-blue-600 hover:underline"
                      >
                        browse
                      </button>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept={ACCEPTED_MIME.join(",")}
                      className="hidden"
                      onChange={onBulkFileInputChange}
                    />
                    <div className="text-[10px] sm:text-xs text-slate-500">
                      Up to {MAX_FILES} files • {humanSize(MAX_FILE_SIZE)} each • Total under {humanSize(MAX_TOTAL_SIZE)}
                    </div>
                  </div>

                  {bulkErrors.files && <p className="text-red-600 text-[11px] sm:text-xs mt-1">{bulkErrors.files}</p>}

                  {!!previews.length && (
                    <ul className="mt-3 divide-y rounded-lg border border-slate-200 overflow-hidden">
                      {previews.map((p, i) => (
                        <li key={`${p.name}-${i}`} className="flex items-center gap-3 p-3">
                          <div className="w-12 h-12 flex items-center justify-center rounded bg-slate-100 overflow-hidden">
                            {p.isImage ? (
                              <img src={p.url} alt={p.name} className="w-full h-full object-cover" />
                            ) : (
                              <span className="text-xs text-slate-600">{p.name.split(".").pop()?.toUpperCase()}</span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs sm:text-sm font-medium text-slate-900 truncate">{p.name}</p>
                            <p className="text-[11px] sm:text-xs text-slate-500">{humanSize(p.size)}</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => onBulkRemoveFile(i)}
                            className="inline-flex items-center gap-1 text-xs sm:text-sm text-red-600 hover:text-red-700"
                            title="Remove"
                          >
                            <FaTrash /> Remove
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Terms */}
                <div className="md:col-span-2">
                  <label className="inline-flex items-start gap-2 text-[11px] sm:text-xs text-slate-700">
                    <input
                      type="checkbox"
                      name="agree"
                      checked={form.agree}
                      onChange={onBulkChange}
                      className="mt-0.5"
                    />
                    <span>
                      I confirm that the details provided are correct and I agree to be contacted by OGCS / BuildPro-OGCS
                      and verified sellers for quotation purposes.
                    </span>
                  </label>
                  {bulkErrors.agree && <p className="text-red-600 text-[11px] sm:text-xs mt-1">{bulkErrors.agree}</p>}
                </div>
              </div>

              {/* Status */}
              {bulkStatus.msg && (
                <div
                  role="status"
                  aria-live="polite"
                  className={`mt-4 rounded-lg px-4 py-3 text-xs sm:text-sm ${
                    bulkStatus.type === "success"
                      ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                      : bulkStatus.type === "error"
                      ? "bg-red-50 text-red-700 border border-red-200"
                      : "bg-slate-50 text-slate-700 border border-slate-200"
                  }`}
                >
                  {bulkStatus.type === "success" ? (
                    <span className="inline-flex items-center gap-2">
                      <FaCheckCircle /> {bulkStatus.msg}
                    </span>
                  ) : (
                    bulkStatus.msg
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="mt-4 flex items-center justify-end gap-3 pb-4">
                <button
                  type="button"
                  onClick={closeBulk}
                  className="rounded-lg border border-slate-300 px-4 py-2 text-xs sm:text-sm text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={submitBulk}
                  disabled={isSubmitting}
                  className="rounded-lg bg-emerald-600 px-4 py-2 text-xs sm:text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
                >
                  {isSubmitting ? "Submitting..." : "Submit Request"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetails;
